import React, { useEffect, useRef, useState } from "react";
import CommonSelect, { Option } from "../../core/common/commonSelect";
import { DatePicker } from "antd";
import dayjs from 'dayjs';
import api from "../../api";
import { toast,ToastContainer } from 'react-toastify';
import ReactDOM from "react-dom";
import { useFilterData } from "../../context/FilterContext";
import { Tooltip } from "antd";


// ADD types + helpers (below imports)
type OptionalHolidayApi = {
  id: number;
  holidayName: string;
  holiday_from: string; // DD-MM-YYYY
  holiday_to: string;   // DD-MM-YYYY
  is_optional: number;
};
// --- OPTIONAL USAGE STATE FOR THE YEAR (NEW) ---
type LeaveRow = {
  user_id?: number | string;
  id?: number | string; // some APIs use "id" for the user id in the row
  status?: string | number; // "1","2","3","4","5" or numbers
  optional_holiday?: 0 | 1 | number | string;
  start_date?: string; // "YYYY-MM-DD" preferred
  end_date?: string;   // "YYYY-MM-DD"
};
// type OptionalRange = { id: number; name: string; from: string; to: string }; // ISO YYYY-MM-DD
const toIsoFromLabel = (label?: string) =>
  label && dayjs(label, "DD-MMM-YYYY", true).isValid()
    ? dayjs(label, "DD-MMM-YYYY").format("YYYY-MM-DD")
    : "";

type LeaveDetails = {
  showModal1: boolean;
  isEditMode: boolean;
  leaveType: number|undefined;
  leaveDuration: string;
  singleDate: string;
  startDate: string;
  endDate: string;
  halfShortDate: string;
  halfDayType: string;
  reason: string;
  status: string;
  disableAllFields: boolean;
  disableAllExceptStatus?: boolean;
  leaveAppliedDate: string;
  selectedEmployeeId: number | undefined;
  validationErrors?: { leaveDuration?: string };
  userId?: number;
  editId?: number;
  users?: any[];
  optional_holiday?: 0 | 1;
  optional_holiday_taken?:0 | 1;
  userDOB?: string | null;
};

type LeaveModalProps = {
  leaveDetails: LeaveDetails;
  userRole: number;
  isOpen: boolean;
  onClose: () => void;
  onError?: (errorMessage: string) => void;
  fetchLeaveRequests?: () => void;
  onSuccess?: () => void;
  children: React.ReactNode;
};

const LeaveModal: React.FC<LeaveModalProps> = ({
  leaveDetails,
  userRole,
  isOpen,
  onClose,
  onError,
  fetchLeaveRequests,
  onSuccess,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const bsModal = useRef<any>(null);

  const [localLeaveDetails, setLocalLeaveDetails] = useState<LeaveDetails>(leaveDetails);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userDOB, setUserDOB] = useState<string | null>(
  leaveDetails.userDOB ?? null
);

const getDobLabelForCurrentYear = (dob?: string | null) => {
  if (!dob) return "";
  const d = dayjs(dob, "DD-MM-YYYY", true);
  if (!d.isValid()) return "";
  return d.year(dayjs().year()).format("DD-MMM-YYYY");
};
const getDobForThisYear = (dob?: string | null) => {
  if (!dob) return null;
  const d = dayjs(dob, "DD-MM-YYYY", true);
  if (!d.isValid()) return null;
  return d.year(dayjs().year());
};

const isBirthdayInCurrentMonth = (dob?: string | null) => {
  const d = getDobForThisYear(dob);
  if (!d) return false;
  return d.month() === dayjs().month();
};

const isBirthdayInPast = (dob?: string | null) => {
  const d = getDobForThisYear(dob);
  if (!d) return false;
  return d.isBefore(dayjs().startOf("day"));
};


  const {
    isEditMode,
    leaveAppliedDate,
    selectedEmployeeId,
    disableAllFields,
    disableAllExceptStatus,
    userId,
    editId,
    users =[]
  } = leaveDetails;

  
  const [localLeaveType, setLocalLeaveType] = useState<number|undefined>(leaveDetails.leaveType);
  const [localLeaveDuration, setLocalLeaveDuration] = useState<string>(leaveDetails.leaveDuration);
  const [localSingleDate, setLocalSingleDate] = useState<string>(leaveDetails.singleDate);
  const [localStartDate, setLocalStartDate] = useState<string>(leaveDetails.startDate);
  const [localEndDate, setLocalEndDate] = useState<string>(leaveDetails.endDate);
  const [localHalfShortDate, setLocalHalfShortDate] = useState<string>(leaveDetails.halfShortDate);
  const [localHalfDayType, setLocalHalfDayType] = useState<string>(leaveDetails.halfDayType);
  const [localReason, setLocalReason] = useState<string>(leaveDetails?.reason || '');
  const [localStatus, setLocalStatus] = useState<string>(leaveDetails.status);
  const [localDisableAllFields, setLocalDisableAllFields] = useState<boolean>(leaveDetails.disableAllFields);
  const [localValidationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [localSelectedEmployeeId, setLocalSelectedEmployeeId] = useState<number | undefined>(undefined);
const OPTIONAL_TAKEN_API_BASE = '/optional-holiday-flag/'; // same endpoint if that's what you have
const [optionalTakenApi, setOptionalTakenApi] = useState<number | null>(null);
// const [weddingTaken, setWeddingTaken] = useState<boolean>(false); // WEDDING LEAVE - commented out
const lastFlagReqIdRef = useRef<number | null>(null);
const originalDurationRef = useRef<string | null>(null);
const countWorkingDaysInc = (from: dayjs.Dayjs, to: dayjs.Dayjs) => {
  let count = 0;
  let d = from.clone();

  while (d.isSame(to, "day") || d.isBefore(to, "day")) {
    const dow = d.day();
    if (dow !== 0 && dow !== 6) {
      count++;
    }
    d = d.add(1, "day");
  }

  return count;
};
const getAdvanceNoticePolicy = (workingLeaveDays: number) => {
  if (workingLeaveDays <= 1.5) {
    return { days: 4, mode: "working" as const };
  }
  if (workingLeaveDays <= 3.5) {
    return { days: 10, mode: "calendar" as const };
  }
  return { days: 30, mode: "calendar" as const };
};


useEffect(() => {
  if (isOpen && isEditMode) {
    originalDurationRef.current = leaveDetails.leaveDuration;
  }
}, [isOpen, isEditMode, leaveDetails.leaveDuration]);

useEffect(() => {
  if (!isOpen) return;

  //  EDIT MODE → populate DOB and short day date
  if (isEditMode) {
    setUserDOB(leaveDetails.userDOB ?? null);

    if (leaveDetails.leaveDuration === "short") {
      setLocalHalfShortDate(
        leaveDetails.userDOB
          ? dayjs(leaveDetails.userDOB, "DD-MM-YYYY")
              .year(dayjs().year())
              .format("DD-MMM-YYYY")
          : ""
      );
    }

    return;
  }

  // CREATE MODE → leave existing logic intact
}, [isOpen, isEditMode, leaveDetails.userDOB, leaveDetails.leaveDuration]);

const initBootstrapTooltipsSafe = () => {
  if (!(window as any)?.bootstrap?.Tooltip) return;

  const els = document.querySelectorAll('[data-bs-toggle="tooltip"]');

  els.forEach((el) => {
    const existing = (window as any).bootstrap.Tooltip.getInstance(el);
    if (!existing) {
     (window as any).bootstrap.Tooltip.getOrCreateInstance(el, {
  trigger: 'hover focus',
});

    }
  });
};

useEffect(() => {
  if (!isOpen) return;

  setTimeout(() => {
    initBootstrapTooltipsSafe();
  }, 0);
}, [isOpen]);

useEffect(() => {
  if (!isOpen) return;

  if (localLeaveDuration === "short") {
    setTimeout(() => {
      initBootstrapTooltipsSafe();
    }, 0);
  }
}, [localLeaveDuration, isOpen]);


// useEffect(() => {
//   if (!isEditMode) return;

//   if (localLeaveDuration === "short" && userDOB) {
//     setLocalHalfShortDate(
//       dayjs(userDOB, "DD-MM-YYYY")
//         .year(dayjs().year())
//         .format("DD-MMM-YYYY")
//     );
//   }
// }, [isEditMode, localLeaveDuration, userDOB]);

  // state for optional and non-optional holidays
const [nonOptionalRanges, setNonOptionalRanges] = useState<OptionalRange[]>([]);
  const toIsoOpt = (d: string) => dayjs(d, "DD-MM-YYYY", true).format("YYYY-MM-DD");
  // robust parser for "DD-MM-YYYY" to "YYYY-MM-DD"
const parseDMYtoISO = (dmy?: string) => {
  if (!dmy) return "";
  const [dd, mm, yyyy] = dmy.split("-").map((s) => parseInt(s, 10));
  if (!yyyy || !mm || !dd) return "";
  const mm2 = String(mm).padStart(2, "0");
  const dd2 = String(dd).padStart(2, "0");
  return `${yyyy}-${mm2}-${dd2}`;
};

const asNum = (v: any) => Number(String(v ?? "").trim() || "0");

const getCurrentYear = () => dayjs().year();

const sameYear = (iso?: string) => {
  if (!iso) return false;
  return dayjs(iso, "YYYY-MM-DD", true).isValid()
    ? dayjs(iso).year() === getCurrentYear()
    : false;
};

/** Collect likely lists of leave rows from filtersData, safely */
const collectPossibleLeaveLists = (fd: any): LeaveRow[] => {
  const pools = [
    fd?.leave_requests,
    fd?.leaves,
    fd?.requests,
    fd?.all_leaves,
    fd?.employee_leaves,
  ].filter(Boolean);

  const out: LeaveRow[] = [];
  for (const p of pools) {
    if (Array.isArray(p)) out.push(...p);
  }
  return out;
};

/**
 * Determine if target has an ACTIVE optional for current year,
 * or has already CONSUMED one (Approved/Sanctioned).
 * ACTIVE means status in {1 (Pending), 2 (Approved), 4 (Sanctioned)}.
 * CONSUMED means status in {2,4}.
 * Revoked (5) / Rejected (3) do NOT block future checkbox.
 */
const getOptionalUsageStateForYear = (
  fd: any,
  targetUserId?: number | string,
  selfStorageFallback = true
) => {
  const rows = collectPossibleLeaveLists(fd).filter(r => {
    const uid = String((r.user_id ?? r.id) ?? "");
    if (!targetUserId || uid !== String(targetUserId)) return false;
    const inYear = sameYear(r.start_date) || sameYear(r.end_date);
    return inYear;
  });

  let hasActive = false; // Pending/Approved/Sanctioned with opt==1
  let consumed  = false; // Approved/Sanctioned with opt==1

  for (const r of rows) {
    const opt = asNum(r.optional_holiday) === 1;
    if (!opt) continue;
    const st = asNum(r.status); // 1 P, 2 A, 3 Rj, 4 S, 5 Rv
    if (st === 2 || st === 4) consumed = true;
    if (st === 1 || st === 2 || st === 4) hasActive = true;
  }

  // If we already found any current-year rows for this user, TRUST rows and ignore LS.
  if (rows.length > 0) return { hasActive, consumed };

  // Otherwise (no rows), optionally consult LS for SELF only.
  // if (selfStorageFallback && targetUserId != null) {
  //   try {
  //     const raw = localStorage.getItem("user");
  //     if (raw) {
  //       const obj = JSON.parse(raw);
  //       const loggedInId = obj?.id;
  //       if (String(loggedInId) === String(targetUserId)) {
  //         const lsTaken =
  //           asNum(obj?.optional_holiday_taken) === 1 ||
  //           asNum(obj?.user?.optional_holiday_taken) === 1;

  //         if (lsTaken) {
  //           // LS implies used once this year. We set consumed only,
  //           // not hasActive (because we don't know status), which still hides the checkbox.
  //           consumed = true;
  //         }
  //       }
  //     }
  //   } catch {}
  // }

  return { hasActive, consumed };
};

  const { filtersData } = useFilterData();
  // ADD new state
  const [optionalHolidayTaken, setOptionalHolidayTaken] = useState<boolean>(false);
  const [optionalRanges, setOptionalRanges] = useState<OptionalRange[]>([]);
  const [showOptionalQuestion, setShowOptionalQuestion] = useState<boolean>(false);
  const [optionalHolidayChoice, setOptionalHolidayChoice] = useState<0 | 1>(0);

  // Fetch logged-in user ID from localStorage when component mounts
  useEffect(() => {
    const loggedIn = JSON.parse(localStorage.getItem("user") || "{}");
    const loggedInUserId = loggedIn?.id;
    console.log("localSelectedEmployeeId in", localSelectedEmployeeId);
    console.log("selectedEmployeeId ", selectedEmployeeId);
    const isAdminApplyingForOthers = userRole === 1 && localSelectedEmployeeId !== loggedInUserId;
    console.log("isAdminApplyingForOthers in", isAdminApplyingForOthers);
    console.log("loggedInUserId in", loggedInUserId);
    console.log("userRole in", userRole);
    // Default localSelectedEmployeeId to logged-in user if available, otherwise to first user or undefined
    setLocalSelectedEmployeeId(
      selectedEmployeeId ?? loggedInUserId ?? (users.length > 0 ? users[0].id : undefined)
    );
  }, [selectedEmployeeId, users, userRole]); // Dependencies ensure this runs when selectedEmployeeId or users change


  useEffect(() => {
    console.log("localSelectedEmployeeId useeffect", localSelectedEmployeeId);
        console.log("selectedEmployeeId useeffect", selectedEmployeeId);
  }, [localSelectedEmployeeId]);
// useEffect(() => {
//   if (!isEditMode) return;

//   // Only manage SHORT DAY if original leave itself was SHORT
//   if (
//     localLeaveDuration !== "short" ||
//     originalDurationRef.current !== "short"
//   ) {
//     return;
//   }

//   if (!userDOB) {
//     setLocalHalfShortDate("");
//     return;
//   }

//   if (!localHalfShortDate) {
//     setLocalHalfShortDate(getDobLabelForCurrentYear(userDOB));
//   }
// }, [userDOB, isEditMode, localLeaveDuration]);



//   useEffect(() => {
//   // If DOB is missing, force-clear short day date
//   if (!userDOB && localLeaveDuration === "short") {
//     setLocalHalfShortDate("");
//   }
// }, [userDOB, localLeaveDuration]);

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedType = parseInt(event.target.value, 10);
    setLocalLeaveType(selectedType);
    setLocalLeaveDetails(prevState => ({ ...prevState, leaveType: selectedType }));
      setOptionalHolidayChoice(0);
  };

  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const duration = event.target.value;
    setLocalLeaveDuration(duration);
    setLocalLeaveDetails(prevState => ({ ...prevState, leaveDuration: duration }));
    // WEDDING LEAVE - commented out
    // if (duration === "wedding") {
    //   setLocalReason("Wedding Leave");
    //   setLocalLeaveDetails(prevState => ({ ...prevState, reason: "Wedding Leave" }));
    // }
      setOptionalHolidayChoice(0);
  // ===== EDIT MODE =====
  if (isEditMode) {
    const original = originalDurationRef.current;

    // ---- SHORT DAY ----
if (duration === "short") {
  setLocalHalfShortDate(
    userDOB
      ? dayjs(userDOB, "DD-MM-YYYY")
          .year(dayjs().year())
          .format("DD-MMM-YYYY")
      : ""
  );
  return;
}



  // ---- HALF DAY ----
if (duration === "half") {
  // In edit mode:
  // Half day should inherit ONLY from Single Day
  if (original === "single") {
    setLocalHalfShortDate(leaveDetails.singleDate || "");
  } else if (original === "half") {
    setLocalHalfShortDate(leaveDetails.halfShortDate || "");
  } else {
    // short / multi → clear
    setLocalHalfShortDate("");
  }
  return;
}



    return;
  }
   // ===== CREATE MODE =====
  if (duration === "short") {
    setLocalHalfShortDate(getDobLabelForCurrentYear(userDOB));
  } else if (duration === "half") {
    setLocalHalfShortDate("");
  }
  };

// Where to read optional holidays from FilterContext
const getOptionalHolidayArray = (fd: any) => {
  if (!fd) return [];
  return (
    fd.optional_holidays ??           // your provided key
    fd.holidays?.optional ??          // possible alt shape
    fd.holidays ??                    // sometimes flat
    []
  );
};

// Where to read ALL holidays (for non-optional detection). Falls back to none.
const getAllHolidaysArray = (fd: any) => {
  if (!fd) return [];
  return (
    fd.holidays_all ??                // preferred if present
    fd.holidays ??                    // may contain both kinds
    []
  );
};


// --- DATE/OPTIONAL HELPERS (REPLACE THE EXISTING BLOCK) ---
type OptionalRange = { id: number; name: string; from: string; to: string }; // ISO YYYY-MM-DD

const addDaysISO = (iso: string, n: number) =>
  dayjs(iso).add(n, "day").format("YYYY-MM-DD");

const isIsoBetweenInc = (d: string, a: string, b: string) => a <= d && d <= b;

const hasWeekendInsideExclusive = (fromIso: string, toIso: string) => {
  if (!fromIso || !toIso || fromIso >= toIso) return false;
  let d = dayjs(fromIso).add(1, "day");
  const end = dayjs(toIso);
  while (d.isBefore(end, "day")) {
    const dow = d.day();
    if (dow === 0 || dow === 6) return true; // Sat/Sun found strictly inside
    d = d.add(1, "day");
  }
  return false;
};

const isOptionalISO = (iso: string, optRanges: OptionalRange[]) =>
  !!iso && optRanges.some(r => isIsoBetweenInc(iso, r.from, r.to));

/** Any optional day strictly inside (start, end) — edges excluded */
const hasOptionalInsideStrict = (startIso: string, endIso: string, opt: OptionalRange[]) => {
  if (!startIso || !endIso || startIso >= endIso) return false;
  let d = dayjs(startIso).add(1, "day");
  const end = dayjs(endIso);
  while (d.isBefore(end, "day")) {
    const iso = d.format("YYYY-MM-DD");
    if (isOptionalISO(iso, opt)) return true;
    d = d.add(1, "day");
  }
  return false;
};

const edgeTouchesOptional = (startIso: string, endIso: string, opt: OptionalRange[]) => {
  const sHit = !!startIso && isOptionalISO(startIso, opt);
  const eHit = !!endIso && isOptionalISO(endIso, opt);
  return { sHit, eHit, anyEdge: sHit || eHit, bothEdges: sHit && !!endIso && isOptionalISO(endIso, opt) };
};

/** Entire window only weekdays (Mon–Fri). Inclusive. */
const allWeekdaysOnlyInc = (fromIso: string, toIso: string) => {
  if (!fromIso || !toIso || fromIso > toIso) return false;
  let d = dayjs(fromIso);
  const end = dayjs(toIso);
  while (d.isSame(end, "day") || d.isBefore(end, "day")) {
    const dow = d.day();
    if (dow === 0 || dow === 6) return false;
    d = d.add(1, "day");
  }
  return true;
};

const fetchOptionalTaken = async (userId?: number) => {
  if (!userId) { setOptionalTakenApi(null); return; }

  // de-dupe: don’t call again for same id
  if (lastFlagReqIdRef.current === userId) return;
  lastFlagReqIdRef.current = userId;

  try {
   const res: any = await api(`${OPTIONAL_TAKEN_API_BASE}${userId}`, { method: 'GET' });
     const taken = Number(res?.optional_holiday);
     setOptionalTakenApi(Number.isFinite(taken) ? taken : null);
     // setWeddingTaken(Number(res?.wedding_taken) === 1); // WEDDING LEAVE - commented out
     if (res?.date_of_birth) {
      setUserDOB(res.date_of_birth); // DD-MM-YYYY
    }
  } catch (e) {
    console.error('optional flag check failed', e);
   setOptionalTakenApi(null);
   // setWeddingTaken(false); // WEDDING LEAVE - commented out
  }
};
const addWorkingDays = (start: dayjs.Dayjs, days: number) => {
  let count = 0;
  let d = start.clone();

  // Count required working days
  while (count < days) {
    d = d.add(1, 'day');
    if (d.day() !== 0 && d.day() !== 6) {
      count++;
    }
  }

  return d.startOf('day'); 
};


useEffect(() => {
  console.log("LeaveModal mounted");
}, []);

// Try to find optional_holiday_taken for an arbitrary employee from filtersData lists.
// Fallback to reading the logged-in user's stored flag only when it's "self".
// Return whether the TARGET employee has already taken the optional holiday.
// - For SELF (target === logged-in), prefer localStorage "user" payload (top or nested).
// - For OTHERS, look in filtersData lists per-employee. If absent -> assume not taken (false).
const getOptionalTakenForUser = (
  fd: any,
  targetId: number | undefined,
  loggedInId: number | undefined
): boolean => {
  if (!targetId) return false;

  // SELF (logged-in employee applying for themselves)
  if (loggedInId && String(targetId) === String(loggedInId)) {
    try {
      const raw = localStorage.getItem("user");
      const obj = raw ? JSON.parse(raw) : {};
      const top = obj?.optional_holiday_taken;
      const nested = obj?.user?.optional_holiday_taken;

      if (typeof top !== "undefined") return Number(top) === 1;
      if (typeof nested !== "undefined") return Number(nested) === 1;
    } catch {}

    // final fallback: try to find self in filtersData lists
    const pools = [fd?.names, fd?.filter_employees, fd?.employees, fd?.all_employees].filter(Boolean) as any[][];
    for (const list of pools) {
      const me = Array.isArray(list) ? list.find(u => String(u?.id) === String(loggedInId)) : undefined;
      if (me && typeof me.optional_holiday_taken !== "undefined") {
        return Number(me.optional_holiday_taken) === 1;
      }
    }
    return false; // unknown -> treat as not taken (show checkbox)
  }

  // OTHER employee (admin applying for someone else)
  const pools = [fd?.names, fd?.filter_employees, fd?.employees, fd?.all_employees].filter(Boolean) as any[][];
  for (const list of pools) {
    const hit = Array.isArray(list) ? list.find(u => String(u?.id) === String(targetId)) : undefined;
    if (hit && typeof hit.optional_holiday_taken !== "undefined") {
      return Number(hit.optional_holiday_taken) === 1;
    }
  }
  return false; // unknown -> treat as not taken (show checkbox)
};


  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isOpen]);


  const handleDateChange = (date: dayjs.Dayjs | null, name: keyof LeaveDetails) => {
    const value = date ? dayjs(date).format("DD-MMM-YYYY") : '';  
  
    switch (name) {
      case 'singleDate':
        setLocalSingleDate(value);
        break;
      case 'startDate':
        setLocalStartDate(value);
        break;
      case 'endDate':
        setLocalEndDate(value);
        break;
      case 'halfShortDate':
        setLocalHalfShortDate(value);
        break;
    }
  
    setLocalLeaveDetails(prev => ({ ...prev, [name]: value }));
  };
  

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement| HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    // Update the respective local state for each field
    switch (name) {
      case 'halfDayType':
        setLocalLeaveDetails(prev => ({ ...prev, halfDayType: value }));
        break;
      case 'reason':
        setLocalReason(value); // Update localReason state
        break;
      default:
        break;
    }

    // Update the overall leave details state
    setLocalLeaveDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleHalfDayTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalLeaveDetails(prev => ({ ...prev, halfDayType: value }));
    setLocalHalfDayType(value);
  };
  
  const loggedInUserString = localStorage.getItem("user");

  // Check if the value is not null before parsing it
  const loggedInUser = loggedInUserString ? JSON.parse(loggedInUserString) : null;
  
  // Get the logged-in employee ID
  const loggedInEmployeeId = loggedInUser?.id;
  
  // Define options
  const allOptions: Option[] = [
    { value: "1", label: "Pending" },
    { value: "2", label: "Approved" },
    { value: "3", label: "Rejected" },
    { value: "4", label: "Sanctioned" },
    { value: "5", label: "Revoked" },
  ];
  
  // Filter visible options based on conditions
  const visibleOptions = allOptions.filter(option => {
  
    if (userRole === 1) {
      // Admin: Allow all statuses if the selected user is not the logged-in user
      return userId === loggedInEmployeeId
        ? option.value === "1" || option.value === "5" // Only show Pending and Revoked for self
        : true; // Admin sees all options for others
    } else {
      // Regular user: Only allow Pending, Approved, and Revoked for self; No Sanctioned status
      return userId === loggedInEmployeeId
        ? option.value === "1" || option.value === "5" // Only show Pending and Revoked for self
        : option.value !== "4"; // Hide Sanctioned status for others
    }
  });
  

  useEffect(() => {
  const modalEl = modalRef.current;

  if (isOpen && modalEl) {
    // Create modal instance
    bsModal.current = new window.bootstrap.Modal(modalEl, {
      backdrop: 'static',
      keyboard: false,
    });

    bsModal.current.show();

    const handleHide = () => {
      onClose();
    };

    // Listen for modal hide
    modalEl.addEventListener("hidden.bs.modal", handleHide);

    // Cleanup
    return () => {
      modalEl.removeEventListener("hidden.bs.modal", handleHide);
      bsModal.current?.hide?.();
      bsModal.current = null;
    };
  }
}, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setLocalLeaveType(leaveDetails.leaveType);
      setLocalLeaveDuration(leaveDetails.leaveDuration);
      setLocalSingleDate(leaveDetails.singleDate);
      setLocalStartDate(leaveDetails.startDate);
      setLocalEndDate(leaveDetails.endDate);
  //  setLocalHalfShortDate(leaveDetails.halfShortDate);
       if (leaveDetails.leaveDuration === "short") {
        setLocalHalfShortDate(
          leaveDetails.userDOB
            ? dayjs(leaveDetails.userDOB, "DD-MM-YYYY")
                .year(dayjs().year())
                .format("DD-MMM-YYYY")
            : ""
        );
      } 
      else if (leaveDetails.leaveDuration === "half") {
        setLocalHalfShortDate(leaveDetails.halfShortDate);
      } 
      else {
        setLocalHalfShortDate("");
      }
      setLocalHalfDayType(leaveDetails.halfDayType);
      setLocalReason(leaveDetails.reason);
      setLocalStatus(leaveDetails.status);
      setLocalDisableAllFields(leaveDetails.disableAllFields);
    // If editing, pre-check based on saved value, but DO NOT force visibility.
// Visibility will be decided by date logic in the next effect.
if (leaveDetails.isEditMode) {
  setOptionalHolidayChoice((leaveDetails.optional_holiday as 0 | 1) ?? 0);
} else {
  setOptionalHolidayChoice(0);
}

    }
  }, [isOpen, leaveDetails]);
  
useEffect(() => {
  if (!isOpen) {
    lastFlagReqIdRef.current = null;
   setOptionalTakenApi(null);
   // setWeddingTaken(false); // WEDDING LEAVE - commented out
  }
}, [isOpen]);

// Use data already loaded in FilterContext + localStorage flag
// Load "taken" flag and optional holiday ranges when modal opens
useEffect(() => {
  if (!isOpen) return;

  // figure out target employee: admin may pick someone else
  const loggedIn = JSON.parse(localStorage.getItem("user") || "{}");
  const loggedInId = loggedIn?.id;
  const targetId =
    localSelectedEmployeeId ??
    selectedEmployeeId ??
    loggedInId;

  // 1) per-target flag
  const takenForTarget = getOptionalTakenForUser(filtersData, targetId, loggedInId);
  setOptionalHolidayTaken(takenForTarget);

  // 2) OPTIONAL ranges (unchanged)
  const optList = getOptionalHolidayArray(filtersData);
  const optRanges: OptionalRange[] = (Array.isArray(optList) ? optList : [])
    .filter((h: OptionalHolidayApi) => Number(h.is_optional) === 1)
    .map((h: OptionalHolidayApi) => ({
      id: h.id,
      name: h.holidayName,
      from: parseDMYtoISO(h.holiday_from),
      to: parseDMYtoISO(h.holiday_to || h.holiday_from),
    }))
    .filter((r) => r.from && r.to);
  setOptionalRanges(optRanges);

  // 3) NON-OPTIONAL ranges (unchanged)
  const all = getAllHolidaysArray(filtersData);
  const nonOptRanges: OptionalRange[] = (Array.isArray(all) ? all : [])
    .filter((h: OptionalHolidayApi) => Number(h.is_optional) !== 1)
    .map((h: OptionalHolidayApi) => ({
      id: h.id,
      name: h.holidayName,
      from: parseDMYtoISO((h as any).holiday_from ?? (h as any).from ?? ""),
      to: parseDMYtoISO((h as any).holiday_to ?? (h as any).to ?? (h as any).holiday_from ?? ""),
    }))
    .filter((r) => r.from && r.to);
  setNonOptionalRanges(nonOptRanges);
}, [isOpen, filtersData, localSelectedEmployeeId, selectedEmployeeId]);

useEffect(() => {
  if (!isOpen || isEditMode) return;
  const loggedIn = JSON.parse(localStorage.getItem("user") || "{}");
  const targetId = (localSelectedEmployeeId ?? selectedEmployeeId ?? loggedIn?.id) as number | undefined;
  fetchOptionalTaken(targetId);
}, [isOpen, isEditMode, localSelectedEmployeeId, selectedEmployeeId]);

// ADD: decide whether to show the "optional holiday" question
// --- DECISION EFFECT (REPLACE YOUR EXISTING SHOW/HIDE useEffect) ---
useEffect(() => {
  // 0) Reset by default
  let show = false;
  let choice: 0 | 1 = 0;

    // --- EDIT MODE SPECIAL CASES ---
  if (isEditMode) {
    const flag = (leaveDetails as any)?.optional_holiday_taken_flag;
    const optHoliday = leaveDetails?.optional_holiday;
    const st = Number(leaveDetails?.status);

    // Case 1: Flag taken but current row not optional -> hide
    if (flag === 1 && optHoliday === 0) {
      setShowOptionalQuestion(false);
      setOptionalHolidayChoice(0);
      return;
    }

    // Case 2: Flag taken and this leave row IS optional -> show if Approved/Sanctioned
    if (flag === 1 && optHoliday === 1 && (st === 2 || st === 4)) {
      setShowOptionalQuestion(true);
      setOptionalHolidayChoice(1);
      return;
    }
  }
  // --- END EDIT MODE SPECIAL CASES ---
  
  // Only relevant for Leave (not WFH)
  if (localLeaveType !== 1) {
    setShowOptionalQuestion(false);
    setOptionalHolidayChoice(0);
    return;
  }

  // Identify target employee (admin may be applying for others)
  const loggedIn = JSON.parse(localStorage.getItem("user") || "{}");
  const loggedInId = loggedIn?.id;
  const targetId = localSelectedEmployeeId ?? selectedEmployeeId ?? loggedInId;

  // Ranges available?
  const opt = optionalRanges;
  const haveOptCalendar = Array.isArray(opt) && opt.length > 0;

  // --- 1) POLICY / FLAG GATE ---
  // usage in year (from FilterContext)
  const { hasActive, consumed } = getOptionalUsageStateForYear(
    filtersData,
    targetId,
    /* selfStorageFallback */ true
  );

  // By default, you cannot offer if there is an active or consumed optional this year.
  let canOfferByPolicy = !hasActive && !consumed;

  // ADD modal: Use /optional-holiday-flag/{id} only as a gate (do not return early).
  if (!isEditMode && optionalTakenApi !== null) {
    // 0 => can offer, 1 => cannot offer
    canOfferByPolicy = canOfferByPolicy && Number(optionalTakenApi) === 0;
  }


  if (!canOfferByPolicy) {
    setShowOptionalQuestion(false);
    setOptionalHolidayChoice(0);
    return;
  }

  // --- 2) DATE INTENT ---
  // Only decide to show checkbox if the selected dates actually qualify.
  const singleIso = toIsoFromLabel(localSingleDate);
  const startIso  = toIsoFromLabel(localStartDate);
  const endIso    = toIsoFromLabel(localEndDate) || startIso;

  // If we don't have holiday calendar yet, don't show (prevents false positives on first load)
  if (!haveOptCalendar) {
    setShowOptionalQuestion(false);
    setOptionalHolidayChoice(0);
    return;
  }

  let wantByDate = false;

  if (localLeaveDuration === "single") {
    // Single-day must itself be an optional date
    wantByDate = !!singleIso && isOptionalISO(singleIso, opt);
  } else if (localLeaveDuration === "multi") {
    if (!startIso || !endIso) {
      wantByDate = false;
    } else {
      const { anyEdge } = edgeTouchesOptional(startIso, endIso, opt);
      const hasOptInside = hasOptionalInsideStrict(startIso, endIso, opt);
      const weekendInside = hasWeekendInsideExclusive(startIso, endIso);

      // Your rules:
      // - If ANY edge is optional => SHOW (even with weekends/other optionals inside)
      // - Else if optional strictly inside (sandwich) => HIDE
      // - Else if weekend strictly inside => HIDE
      // - Else if all weekdays => SHOW
      if (anyEdge) {
        wantByDate = true;
      } else if (hasOptInside) {
        wantByDate = false;
      } else if (weekendInside) {
        wantByDate = false;
      } else {
        wantByDate = false;
      }
    }
  } else {
    // half / short / others => no optional question
    wantByDate = false;
  }

  // --- 3) FINAL DECISION ---
  show = canOfferByPolicy && wantByDate;

  setShowOptionalQuestion(show);
  setOptionalHolidayChoice(show ? ((leaveDetails?.optional_holiday as 0 | 1) ?? optionalHolidayChoice) : 0);
}, [
  isOpen,
  filtersData,
  localLeaveType,
  localLeaveDuration,
  localSingleDate,
  localStartDate,
  localEndDate,
  localSelectedEmployeeId,
  selectedEmployeeId,
  optionalRanges,
  nonOptionalRanges, // kept in deps; not used directly now
  leaveDetails.isEditMode,
  leaveDetails.optional_holiday,
  leaveDetails.optional_holiday_taken,
  (leaveDetails as any)?.optional_holiday_taken_flag,
  leaveDetails.status,
  optionalTakenApi,
]);







// ADD: date cell decorator for optional holidays
const dateRenderOpt = (current: dayjs.Dayjs) => {
  // If leave type = 2, skip highlighting
  if (localLeaveType === 2) {
    return (
      <div>
        <div className="ant-picker-cell-inner">{current.date()}</div>
      </div>
    );
  }
  const iso = current.format("YYYY-MM-DD");
  const isOpt =
    optionalRanges.length > 0 &&
    optionalRanges.some((r) => iso >= r.from && iso <= r.to);
  const style: React.CSSProperties = isOpt
    ? { background: "#fff2cc", borderRadius: 4 }
    : {};
  return (
    <div style={style}>
      <div className="ant-picker-cell-inner">{current.date()}</div>
    </div>
  );
};
// you already have these:
const todayStart = dayjs().startOf("day");
const isPastLabel = (label?: string) =>
  !!label && dayjs(label, "DD-MMM-YYYY").startOf("day").isBefore(todayStart);
const isPastISO = (iso?: string) =>
  !!iso && dayjs(iso, "YYYY-MM-DD", true).isBefore(todayStart);

// NEW: compare two DD-MMM-YYYY labels for same day (used in edit mode)
const sameLabelDay = (a?: string, b?: string) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const A = dayjs(a, "DD-MMM-YYYY", true);
  const B = dayjs(b, "DD-MMM-YYYY", true);
  if (!A.isValid() || !B.isValid()) return a === b;
  return A.isSame(B, "day");
};

// NEW: centralize “block past for self (employee)” for DatePicker
const shouldDisablePastForSelf = (current: dayjs.Dayjs) => {
  const { actingForOthers } = getActorContext();
  // Only block past dates when the user is editing/applying for THEMSELVES
  return !actingForOthers && current.startOf("day").isBefore(todayStart);
};

// Who is the target of this leave row, and am I acting for someone else?
const getActorContext = () => {
  const logged = JSON.parse(localStorage.getItem("user") || "{}");
  const loggedId = logged?.id;
  const role = logged?.role_name;

  // In EDIT use the leave owner first; otherwise fall back to selector / self
  const targetId = isEditMode
    ? (leaveDetails?.userId ?? localSelectedEmployeeId ?? selectedEmployeeId ?? loggedId)
    : (localSelectedEmployeeId ?? selectedEmployeeId ?? loggedId);

  // Acting-for-others if target != me and I’m admin or reporting-head
  const actingForOthers =
    String(targetId) !== String(loggedId) &&
    (role === 1 || role === 2);

  return { role, loggedId, targetId, actingForOthers };
};

  const getModalContainer = () => {
    const modalElement = document.getElementById('modal-datepicker');
    return modalElement ? modalElement : document.body;
  };
  
  console.log("localEndDate", localEndDate);

  // Inside your functional component
const handleClose = () => {
    if (onClose) {
      onClose(); // Call the parent-provided close handler
    }
  };
  const getDobISO = () =>
  userDOB ? dayjs(userDOB, "DD-MM-YYYY").format("YYYY-MM-DD") : null;

  const isCurrentMonth = (d: dayjs.Dayjs) =>
  d.month() === dayjs().month() && d.year() === dayjs().year();
  const validateFourWorkingDaysRule = (
    labelDate: string,
    originalLabelDate?: string
  ) => {
    const leaveDate = dayjs(labelDate, "DD-MMM-YYYY").startOf("day");
    const today = dayjs().startOf("day");

    const { actingForOthers } = getActorContext();

    // EDIT MODE: allow unchanged date
    const isSameAsOriginal =
      isEditMode && sameLabelDay(labelDate, originalLabelDate);

    if (isSameAsOriginal || actingForOthers) return null;

    // Past date block
    if (leaveDate.isBefore(today)) {
      return "Past dates are not allowed.";
    }

    // 4 working days rule
    const minDate = addWorkingDays(today, 4);
    if (leaveDate.isBefore(minDate)) {
      return "Half day Leave must be applied at least 4 working days in advance.";
    }

    return null;
  };


  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage("");
    setValidationErrors({});
    setIsSaving(true);

    const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
    const loggedInUserId = loggedInUser.id;
    const userRole = loggedInUser.role_name;
    const isReportingHeadApprovingForOthers = userRole === 2 && localSelectedEmployeeId !== loggedInUserId;
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);


    const {
      selectedEmployeeId,
    } = leaveDetails;

    const isAdminApplyingForOthers = userRole === 1 && localSelectedEmployeeId !== loggedInUserId;
   
    const errors: Record<string, string> = {};

    const today = dayjs().startOf("day");

    // === VALIDATIONS ===
    if (!localLeaveType) errors.leaveType = "Leave type is required.";
    if (!localLeaveDuration) errors.leaveDuration = "Leave duration is required.";
    if (!localReason) errors.reason = "Reason is required.";

    const validateDateAdvance = (dateStr: string, days: number, key: string, label: string) => {
      const date = dayjs(dateStr, "DD-MMM-YYYY").startOf("day");
      const diffDays = date.diff(today, "day");

      if (!isAdminApplyingForOthers && !isReportingHeadApprovingForOthers && diffDays < days) {
        errors[key] = `${label} must be applied at least ${days} day(s) in advance.`;
      }

      if (!isAdminApplyingForOthers && !isReportingHeadApprovingForOthers && date.isSame(today)) {
        errors[key] = `${label} cannot be taken on the same day.`;
      }
    };

    if (localLeaveType === 2) {
      if (localLeaveDuration === "single") {
        if (!localSingleDate) {
          errors.localSingleDate = "WFH Requires a date.";
        } else {
          validateDateAdvance(localSingleDate, 1, "localSingleDate", "WFH");
        }
      }

      if (localLeaveDuration === "half") {
        if (!localHalfShortDate) errors.localHalfShortDate = "WFH Requires a date.";
        if (!localHalfDayType) errors.localHalfDayType = "WFH Half day type is required.";
        if (localHalfShortDate) validateDateAdvance(localHalfShortDate, 1, "localHalfShortDate", "WFH");
      }
    } else if (localLeaveType === 1) {
      if (localLeaveDuration === "single") {
        if (!localSingleDate) {
          errors.localSingleDate = "Single day leave requires a date.";
        } else {
          const leaveDate = dayjs(localSingleDate, "DD-MMM-YYYY").startOf("day");

          const dayOfWeek = today.day(); 
          const daysSinceMonday = (dayOfWeek + 6) % 7; 
          const startOfWeek = today.subtract(daysSinceMonday, "day").startOf("day");
          const endOfWeek = startOfWeek.add(6, "day");

          // Apply validation only if NOT admin applying for others
          const isLeaveInCurrentWeek =
            leaveDate.isSame(startOfWeek) ||
            (leaveDate.isAfter(startOfWeek) && leaveDate.isBefore(endOfWeek.add(1, "day")));

          // if (!isAdminApplyingForOthers && !isReportingHeadApprovingForOthers && isLeaveInCurrentWeek) {
          //   errors.localSingleDate = "Single-day leave must be requested before the current week.";
          // }

          const { actingForOthers } = getActorContext();

          // Enforce rule for SELF (create + edit)
         //  EDIT MODE: allow unchanged past/today date
          const isSameAsOriginal =
            isEditMode &&
            sameLabelDay(localSingleDate, leaveDetails.singleDate);

          // Block past date ONLY if user changed it
          if (!isSameAsOriginal && !actingForOthers && leaveDate.isBefore(today)) {
            errors.localSingleDate = "Past dates are not allowed.";
          }

          // Apply 4-working-day rule ONLY if date changed
          if (!isSameAsOriginal && !actingForOthers) {
            const minDate = addWorkingDays(today, 4);
            if (leaveDate.isBefore(minDate)) {
              errors.localSingleDate =
                "Single-day leave must be applied at least 4 working days in advance.";
            }
          }

        }
      }

      if (localLeaveDuration === "multi") {
        if (!localStartDate) errors.localStartDate = "Start date is required for multi-day leave.";
        if (!localEndDate) errors.localEndDate = "End date is required for multi-day leave.";

        if (localStartDate && localEndDate) {
          const startDate = dayjs(localStartDate, "DD-MMM-YYYY").startOf("day");
          const endDate = dayjs(localEndDate, "DD-MMM-YYYY").startOf("day");
      //     const leaveDuration = endDate.diff(startDate, "day") + 1; // inclusive of both dates

      //     const advanceNoticeRequired =
      //       leaveDuration >= 4 ? 30 : leaveDuration >= 2 ? 10 : 0;

      //     const daysUntilStart = startDate.diff(today, "day");

      //     if (!isAdminApplyingForOthers && !isReportingHeadApprovingForOthers && advanceNoticeRequired > 0 && daysUntilStart < advanceNoticeRequired) {
      //       errors.localStartDate = `Multi-day leave of ${leaveDuration} day(s) must be requested at least ${advanceNoticeRequired} day(s) in advance.`;
      //     }
      //   }
      // } 
        //  Count WORKING DAYS of the leave itself
        const workingLeaveDays = countWorkingDaysInc(startDate, endDate);

        //  Decide policy
        const policy = getAdvanceNoticePolicy(workingLeaveDays);
        const calendarLeaveDays =
          endDate.diff(startDate, "day") + 1;

        const { actingForOthers } = getActorContext();

        // EDIT MODE: allow unchanged dates
        const isSameAsOriginal =
          isEditMode &&
          sameLabelDay(localStartDate, leaveDetails.startDate) &&
          sameLabelDay(localEndDate, leaveDetails.endDate);

        // Calculate advance notice based on MODE
        let isViolation = false;

        if (!isSameAsOriginal && !actingForOthers) {
            let isViolation = false;

            if (policy.mode === "working") {
              const workingDaysUntilStart =
                countWorkingDaysInc(today.add(1, "day"), startDate);
              isViolation = workingDaysUntilStart < policy.days;
            } else {
              const calendarDaysUntilStart =
                startDate.diff(today, "day");
              isViolation = calendarDaysUntilStart < policy.days;
            }

            if (isViolation) {
              if (policy.mode === "working") {
                errors.localStartDate =
                  `Multi-day leave `+
                  `must be applied at least ${policy.days} working days in advance.`;
              } else {
                errors.localStartDate =
                  `Multi-day leave ` +
                  `(${calendarLeaveDays} calendar days) ` +
                  `must be applied at least ${policy.days} calendar days in advance.`;
              }
            }
          }

        }
        }
      // SHORT DAY BIRTHDAY VALIDATIONS
      if (localLeaveDuration === "short") {
        const { actingForOthers } = getActorContext();

        if (!userDOB) {
          errors.localHalfShortDate = "Date of birth not available.";
        }

        //  Past birthday → blocked for EVERYONE
        else if (isBirthdayInPast(userDOB)) {
          errors.localHalfShortDate =
            "You cannot apply short day for a past birthday.";
        }

        //  Birthday month restriction → ONLY for self employee
        else if (!actingForOthers && !isBirthdayInCurrentMonth(userDOB)) {
          errors.localHalfShortDate =
            "Short day can be applied only in your birthday month.";
        }
      }




      if (localLeaveDuration === "short" ) {
        const capitalizedLeaveDuration = capitalize(localLeaveDuration);
        if (!localHalfShortDate) errors.localHalfShortDate = `A date is required for ${capitalizedLeaveDuration} leave.`;
        if (!localHalfDayType) errors.localHalfDayType = `Half day type is required for ${capitalizedLeaveDuration} leave.`;
      //  if (localHalfShortDate) {;
      //     validateDateAdvance(localHalfShortDate, 1, "localHalfShortDate", `${capitalizedLeaveDuration} leave`);
      //   }
        if (localLeaveDuration !== "short" && localHalfShortDate) {
        validateDateAdvance(
          localHalfShortDate,
          1,
          "localHalfShortDate",
          `${capitalizedLeaveDuration} leave`
        );
      }

      }
      if (localLeaveDuration === "half") {
          if (!localHalfShortDate) {
            errors.localHalfShortDate = "Half day leave requires a date.";
          } else {
            const err = validateFourWorkingDaysRule(
              localHalfShortDate,
              leaveDetails.halfShortDate
            );
            if (err) errors.localHalfShortDate = err;
          }

          if (!localHalfDayType) {
            errors.localHalfDayType = "Half day type is required.";
          }
        }


      // WEDDING LEAVE - commented out
      // if (localLeaveDuration === "wedding") {
      //   if (!localStartDate) errors.localStartDate = "Start date is required for wedding leave.";
      //   if (!localEndDate) errors.localEndDate = "End date is required for wedding leave.";
      //
      //   if (localStartDate && localEndDate) {
      //     const { actingForOthers } = getActorContext();
      //     const weddingStart = dayjs(localStartDate, "DD-MMM-YYYY").startOf("day");
      //     const weddingEnd = dayjs(localEndDate, "DD-MMM-YYYY").startOf("day");
      //
      //     if (!actingForOthers) {
      //       const daysUntilStart = weddingStart.diff(today, "day");
      //       if (daysUntilStart < 30) {
      //         errors.localStartDate = "Wedding leave must be applied at least 30 days in advance.";
      //       }
      //     }
      //   }
      // }

      // NEW: hard-stop for optional dates in the past (employees only)
{
  const singleIso = toIsoFromLabel(localSingleDate);
  const startIso  = toIsoFromLabel(localStartDate);
  const endIso    = toIsoFromLabel(localEndDate) || singleIso;

// === Block any past date for employees (create + edit). ===
const { actingForOthers } = getActorContext();
const isSelfEmployee = !actingForOthers;

if (isSelfEmployee) {
  const pastMsg = "Past dates are not allowed.";
  if (localLeaveDuration === "single") {
    if (isPastLabel(localSingleDate)) errors.localSingleDate = pastMsg;
  } else if (localLeaveDuration === "multi") { // WEDDING LEAVE: was "multi" || "wedding"
    if (isPastLabel(localStartDate)) errors.localStartDate = pastMsg;
    if (isPastLabel(localEndDate))   errors.localEndDate   = pastMsg;
  } else if (localLeaveDuration === "half" || localLeaveDuration === "short") {
    if (isPastLabel(localHalfShortDate)) errors.localHalfShortDate = pastMsg;
  }
}



}

    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsSaving(false);
      setTimeout(() => setValidationErrors({}), 3000);
      return;
    }

    const formatDate = (dateStr: string) => {
      return dateStr ? dayjs(dateStr, "DD-MMM-YYYY").format("YYYY-MM-DD") : "";
    };

    const submissionData = {
      leave_id: isEditMode ? editId : null,
      id: localSelectedEmployeeId  || loggedInUserId,
      userId: leaveDetails.userId,
      leave: localLeaveDuration,
      type: localLeaveType,
      reason: localReason,
      remarks: "",
      start_date:
      localLeaveDuration === "single"
          ? formatDate(localSingleDate)
          : localLeaveDuration === "multi" // WEDDING LEAVE: was "multi" || "wedding"
          ? formatDate(localStartDate)
          : localLeaveDuration === "half" || localLeaveDuration === "short"
          ? formatDate(localHalfShortDate)
          : "",
      end_date: localLeaveDuration === "multi" ? formatDate(localEndDate) : "", // WEDDING LEAVE: was "multi" || "wedding"
      half_day_type: localHalfDayType === "first-half" ? 1 : localHalfDayType === "second-half" ? 2 : null,
      // is_wedding: localLeaveDuration === "wedding", // WEDDING LEAVE - commented out
      status: localStatus || 1,
     optional_holiday: showOptionalQuestion
  ? optionalHolidayChoice
  : ((leaveDetails.optional_holiday as 0 | 1) ?? 0),
    };

    try {
        const response: any = await api("/save-leave-request", {
            method: "POST",
            body: submissionData,
          });
          
      if (response.message === "success.") {
  try {
    const raw = localStorage.getItem("user");
    const obj = raw ? JSON.parse(raw) : {};
    const loggedInId = obj?.id;
    const targetId = localSelectedEmployeeId || loggedInId;

    // keep LS only for SELF
    if (String(targetId) === String(loggedInId)) {
      if (submissionData.optional_holiday === 1) {
        // checked -> mark taken
        obj.optional_holiday_taken = 1;
        if (obj.user && typeof obj.user === "object") {
          obj.user.optional_holiday_taken = 1;
        }
        setOptionalHolidayTaken(true);
      } else {
        // unchecked -> clear taken
        obj.optional_holiday_taken = 0;
        if (obj.user && typeof obj.user === "object") {
          obj.user.optional_holiday_taken = 0;
        }
        setOptionalHolidayTaken(false);
      }
      localStorage.setItem("user", JSON.stringify(obj));
    }
  } catch {}

        toast.success("Leave request saved successfully.");
        setIsSaving(false);
        handleClose();
        if (isEditMode) {
            fetchLeaveRequests?.();
        } else {
            if (onSuccess) onSuccess();
        }
        // fetchLeaveRequests(); // refresh leave list
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.message || "An error occurred. Please try again later.";
      toast.error(errorMsg);
      setErrorMessage(errorMsg);
      setIsSaving(false);
      if (onError) onError(errorMsg);
      handleClose();
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };
  

  useEffect(() => {
  console.log("LeaveModal users updated:", leaveDetails.users);
}, [leaveDetails.users]);


 return isOpen
  ? ReactDOM.createPortal(
      <>
    <div
      className="modal"
      id="leaveModal"
      tabIndex={-1}
      aria-labelledby="leaveModalLabel"
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="leaveModalLabel">
              {isEditMode ? "Edit Leave Request" : "Request Leave/WFH"}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div className="modal-body">

            {userRole === 1 && !isEditMode && (
                <div className="w-full form-group mb-3">
                    <label className="form-label">Select Employee <span className="text-danger">*</span></label>
                    <CommonSelect
                      options={(users ?? []).map(user => ({
                        value: String(user.id),  
                        label: user.name,
                      }))}
                      value={ 
                        (users ?? [])
                          .map(user => ({
                            value: String(user.id),
                            label: user.name,
                          }))
                          .find(option => option.value === String(localSelectedEmployeeId)) || undefined
                      }
                     onChange={(selectedOption) => {
  if (selectedOption) {
    const newId = parseInt(selectedOption.value, 10);
    setLocalSelectedEmployeeId(newId);
    setUserDOB(null);
    setLocalHalfShortDate("");
    setLocalLeaveDuration(""); // forces re-select
    setLocalHalfDayType("");
    setOptionalHolidayChoice(0);
    setShowOptionalQuestion(false);
     lastFlagReqIdRef.current = null; 
    // if (!isEditMode) fetchOptionalFlag(newId); // re-check flag for newly selected employee
  }
}}    
                      isHeight={true}
                      placeholder="Select a user"
                      isDisabled={false}
                      forceFullWidth={true}
                    />
                </div>
                )}

            {isEditMode && (
              <div className="mb-3">
                <label className="form-label">Leave Applied On</label>
                <input
                  type="text"
                  className="form-control"
                  value={leaveAppliedDate || ""}
                  disabled
                />
              </div>
            )}

            {/* Leave Type */}
            <div className="mb-3">
              <label className="form-label">
                Type <span className="text-danger">*</span>
              </label>
              <div>
                <label className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="leaveType"
                    value="1"
                    checked={localLeaveType === 1}
                    onChange={handleTypeChange}
                    disabled={disableAllFields}
                  />
                  Leave
                </label>

                <label className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="leaveType"
                    value="2"
                    checked={localLeaveType === 2}
                    onChange={handleTypeChange}
                    disabled={disableAllFields}
                  />
                  WFH
                </label>
              </div>

              {localValidationErrors?.leaveType && (
                <div style={{ color: 'red' }}>{localValidationErrors.leaveType}</div>
              )}
            </div>


            {/* Leave Duration */}
            {localLeaveType && (
              <div className="mb-3">
                <label className="form-label">
                  Leave Duration <span className="text-danger">*</span>
                </label>
                <div>
                  {(localLeaveType === 1 || localLeaveType === 2) && (
                    <label className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="leaveDuration"
                        value="single"
                        checked={localLeaveDuration === "single"}
                        onChange={handleDurationChange}
                        disabled={disableAllFields}
                      />
                      Single Day
                    </label>
                  )}

                  {localLeaveType === 1 && (
                    <label className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="leaveDuration"
                        value="multi"
                        checked={localLeaveDuration === "multi"}
                        onChange={handleDurationChange}
                        disabled={disableAllFields}
                      />
                      Multi Day
                    </label>
                  )}

                  {localLeaveType === 1 && (
                    <label className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="leaveDuration"
                        value="short"
                        checked={localLeaveDuration === "short"}
                        onChange={handleDurationChange}
                        disabled={localDisableAllFields}
                      />
                      <span className="ms-1">
                        Short Day
                        <i
                          className="ti ti-info-circle ms-1 text-muted"
                          style={{ cursor: "pointer" }}
                          data-bs-toggle="tooltip"
                          data-bs-trigger="hover click"
                          data-bs-placement="top"
                          tabIndex={0}
                          role="button"
                          title="Short Day can be taken only on your birthday"
                        />
                      </span>
                    </label>
                  )}

                  {(localLeaveType === 1 || localLeaveType === 2) && (
                    <label className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="leaveDuration"
                        value="half"
                        checked={localLeaveDuration === "half"}
                        onChange={handleDurationChange}
                        disabled={disableAllFields}
                      />
                      Half Day
                    </label>
                  )}

                  {/* WEDDING LEAVE - commented out
                  {localLeaveType === 1 && !weddingTaken && (
                    <label className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="leaveDuration"
                        value="wedding"
                        checked={localLeaveDuration === "wedding"}
                        onChange={handleDurationChange}
                        disabled={disableAllFields}
                      />
                      Wedding Leave
                    </label>
                  )}
                  */}
                </div>

                {localValidationErrors?.leaveDuration && (
                  <div style={{ color: 'red' }}>{localValidationErrors.leaveDuration}</div>
                )}
              </div>
            )}

            {/* Dates based on leaveDuration */}
            {localLeaveDuration === 'single' && (
              <div className="form-group mb-3">
                <label className="form-label">Date <span className="text-danger">*</span></label>
                <div className="input-icon-end position-relative">
                <DatePicker
                  className="form-control datetimepicker"
                  format="DD-MMM-YYYY"  // Correct format as string
                  getPopupContainer={getModalContainer}  // Ensure getModalContainer returns a valid element
                  placeholder="DD-MMM-YYYY"
                  value={localSingleDate ? dayjs(localSingleDate, "DD-MMM-YYYY") : null}  // Use dayjs for parsing
                  onChange={(date) => handleDateChange(date, 'singleDate')}  // On change
                  disabled={localDisableAllFields}  // Check if this flag is correctly set
                  dateRender={dateRenderOpt} 
                  disabledDate={(current) => !!current && shouldDisablePastForSelf(current)}
                />
                                            <span className="input-icon-addon">
                                              <i className="ti ti-calendar text-gray-7" />
                                            </span>
                </div>
                {localValidationErrors?.localSingleDate && <div style={{ color: 'red' }}>{localValidationErrors.localSingleDate}</div>}
                {showOptionalQuestion && localLeaveDuration === 'single' && localLeaveType === 1 && (
  <div className="mt-2">
    <label className="form-label d-block">
      Do you want to take the optional holiday?
    </label>
    <div className="form-check">
      <input
        id="opt-holiday-single"
        type="checkbox"
        className="form-check-input"
        checked={optionalHolidayChoice === 1}
        onChange={(e) => setOptionalHolidayChoice(e.target.checked ? 1 : 0)}
        disabled={localDisableAllFields}
      />
      <label className="form-check-label" htmlFor="opt-holiday-single">
        Yes (optional holiday won’t reduce your leave balance)
      </label>
    </div>
  </div>
)}

              </div>
            )}

            {localLeaveDuration === 'multi' && localLeaveType === 1 && ( /* WEDDING LEAVE: was (multi || wedding) */
              <>
                <div className="form-group mb-3">
                  <label className="form-label">From Date <span className="text-danger">*</span></label>
                  <div className="input-icon-end position-relative">
                  <DatePicker
                    className="form-control datetimepicker"
                    format="DD-MMM-YYYY"  // Correct format as string
                    getPopupContainer={getModalContainer}
                    placeholder="DD-MMM-YYYY"
                    value={localStartDate ? dayjs(localStartDate, "DD-MMM-YYYY") : null}  // Use dayjs for parsing
                    onChange={(date) => handleDateChange(date, 'startDate')}
                    disabled={localDisableAllFields}
                disabledDate={(current) => {
    if (!current) return false;
    const afterEnd = localEndDate
      ? current.isAfter(dayjs(localEndDate, "DD-MMM-YYYY").subtract(1, "day"))
      : false;
    const pastForSelf = shouldDisablePastForSelf(current);
    return afterEnd || pastForSelf;
  }}



                      dateRender={dateRenderOpt}  
                  />

                                            <span className="input-icon-addon">
                                              <i className="ti ti-calendar text-gray-7" />
                                            </span>
                </div>
                {localValidationErrors?.localStartDate && <div style={{ color: 'red' }}>{localValidationErrors.localStartDate}</div>}
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">To Date <span className="text-danger">*</span></label>
                  
                  <div className="input-icon-end position-relative">
                  <DatePicker
                    className="form-control datetimepicker"
                    format="DD-MMM-YYYY"  // Correct format as string
                    getPopupContainer={getModalContainer}
                    placeholder="DD-MMM-YYYY"
                    value={localEndDate ? dayjs(localEndDate, "DD-MMM-YYYY") : null}  // Use dayjs for parsing
                    onChange={(date) => handleDateChange(date, 'endDate')}
                    disabled={localDisableAllFields}
                    minDate={
                      localStartDate 
                        ? dayjs(localStartDate, "DD-MMM-YYYY").add(1, 'day')  // Use dayjs for comparison and add 1 day to the startDate
                        : dayjs().add(1, 'day')  // Default to the current date + 1 day if localStartDate is not set
                    }
                      dateRender={dateRenderOpt}  
                 // NEW: minDate is ignored by antd; use disabledDate instead
  disabledDate={(current) => {
    if (!current) return false;
    const beforeOrSameStart = localStartDate
      ? current.isSame(dayjs(localStartDate, "DD-MMM-YYYY"), "day") ||
        current.isBefore(dayjs(localStartDate, "DD-MMM-YYYY"), "day")
      : false;
    const pastForSelf = shouldDisablePastForSelf(current);
    return beforeOrSameStart || pastForSelf;
  }}



                  />

                                            <span className="input-icon-addon">
                                              <i className="ti ti-calendar text-gray-7" />
                                            </span>
                </div>
                {localValidationErrors?.localEndDate && <div style={{ color: 'red' }}>{localValidationErrors.localEndDate}</div>}
                {showOptionalQuestion && localLeaveDuration === 'multi' && (
  <div className="mt-2">
    <label className="form-label d-block">
      Do you want to take the optional holiday?
    </label>
    <div className="form-check">
      <input
        id="opt-holiday-multi"
        type="checkbox"
        className="form-check-input"
        checked={optionalHolidayChoice === 1}
        onChange={(e) => setOptionalHolidayChoice(e.target.checked ? 1 : 0)}
        disabled={localDisableAllFields}
      />
      <label className="form-check-label" htmlFor="opt-holiday-multi">
        Yes (optional holiday won’t reduce your leave balance)
      </label>
    </div>
  </div>
)}

                </div>
              </>
            )}

            {localLeaveDuration === 'half'  && (
              <>
                <div className="form-group mb-3">
                  <label className="form-label">Date <span className="text-danger">*</span></label>
                  <div className="input-icon-end position-relative">
                  <DatePicker
                    className="form-control datetimepicker"
                    format="DD-MMM-YYYY"  // Correct format as string
                    getPopupContainer={getModalContainer}
                    placeholder="DD-MMM-YYYY"
                    value={localHalfShortDate ? dayjs(localHalfShortDate, "DD-MMM-YYYY") : null}  // Use dayjs for parsing
                    onChange={(date) => handleDateChange(date, 'halfShortDate')}
                    disabled={localDisableAllFields}
                    disabledDate={(current) => !!current && shouldDisablePastForSelf(current)}
                    
                  />    
                    <span className="input-icon-addon">
                        <i className="ti ti-calendar text-gray-7" />
                    </span>
                                            
                </div>
                {localValidationErrors?.localHalfShortDate && <div style={{ color: 'red' }}>{localValidationErrors.localHalfShortDate}</div>}
                </div>

                <div className="form-group mb-3">
                <label className="form-label">
                  Half Day Type <span className="text-danger">*</span>
                </label>
                <div className="d-flex gap-3">
                  <label className="form-check" style={{ display: "block" }}>
                    <input
                      type="radio"
                      className="form-check-input"
                      name="halfDayType"
                      value="first-half"
                      checked={localHalfDayType === "first-half"}
                      onChange={handleHalfDayTypeChange}
                      disabled={localDisableAllFields}
                    />
                    First Half
                  </label>

                  <label className="form-check" style={{ display: "block" }}>
                    <input
                      type="radio"
                      className="form-check-input"
                      name="halfDayType"
                      value="second-half"
                      checked={localHalfDayType === "second-half"}
                      onChange={handleHalfDayTypeChange}
                      disabled={localDisableAllFields}
                    />
                    Second Half
                  </label>
                </div>

                {localValidationErrors?.localHalfDayType && (
                  <div style={{ color: "red" }}>{localValidationErrors.localHalfDayType}</div>
                )}
              </div>
              </>
            )}


            {(localLeaveDuration === 'short' && localLeaveType === 1) && (
              <>
                <div className="form-group mb-3">
                  <label className="form-label">Date <span className="text-danger">*</span></label>
                  <div className="input-icon-end position-relative">
                  <DatePicker
                    className="form-control datetimepicker"
                    format="DD-MMM-YYYY"  // Correct format as string
                    getPopupContainer={getModalContainer}
                    placeholder="DD-MMM-YYYY"
                    value={localHalfShortDate ? dayjs(localHalfShortDate, "DD-MMM-YYYY") : null}  // Use dayjs for parsing
                    onChange={(date) => handleDateChange(date, 'halfShortDate')}
                    disabled={localDisableAllFields}
                      // disabledDate={(current) => !!current && shouldDisablePastForSelf(current)}
                     //  THIS IS THE KEY FIX 
                    defaultPickerValue={
                      userDOB ? dayjs(userDOB, "DD-MM-YYYY").year(dayjs().year()) : undefined
                    }

                    disabledDate={(current) => {
                      if (!current || !userDOB) return true;

                      const dob = dayjs(userDOB, "DD-MM-YYYY");

                      // only day + month must match
                      return !(
                        current.date() === dob.date() &&
                        current.month() === dob.month()
                      );
                    }}
                  />

                                            <span className="input-icon-addon">
                                              <i className="ti ti-calendar text-gray-7" />
                                            </span>
                </div>
                {localValidationErrors?.localHalfShortDate && <div style={{ color: 'red' }}>{localValidationErrors.localHalfShortDate}</div>}
                </div>

                <div className="form-group mb-3">
                <label className="form-label">
                  Half Day Type <span className="text-danger">*</span>
                </label>
                <div className="d-flex gap-3">
                  <label className="form-check" style={{ display: "block" }}>
                    <input
                      type="radio"
                      className="form-check-input"
                      name="halfDayType"
                      value="first-half"
                      checked={localHalfDayType === "first-half"}
                      onChange={handleHalfDayTypeChange}
                      disabled={localDisableAllFields}
                    />
                    First Half
                  </label>

                  <label className="form-check" style={{ display: "block" }}>
                    <input
                      type="radio"
                      className="form-check-input"
                      name="halfDayType"
                      value="second-half"
                      checked={localHalfDayType === "second-half"}
                      onChange={handleHalfDayTypeChange}
                      disabled={localDisableAllFields}
                    />
                    Second Half
                  </label>
                </div>

                {localValidationErrors?.localHalfDayType && (
                  <div style={{ color: "red" }}>{localValidationErrors.localHalfDayType}</div>
                )}
              </div>
              </>
            )}

            {/* Status Dropdown */}
            {(isEditMode || (userRole === 1 && String(localSelectedEmployeeId) !== String(JSON.parse(localStorage.getItem("user") || "{}")?.id))) && (
              <div className="w-full form-group mb-3">
                <label className="form-label">Status <span className="text-danger">*</span></label>
                <CommonSelect
                  options={visibleOptions}
                  value={allOptions.find(option => option.value === localStatus) || undefined}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setLocalStatus(selectedOption.value);
                      setLocalLeaveDetails(prev => ({ ...prev, status: selectedOption.value }));
                    }
                  }}
                  isHeight={true}
                  placeholder="Select Status"
                  isDisabled={disableAllExceptStatus ? false : localDisableAllFields}
                  forceFullWidth={true}
                />
                {localValidationErrors?.status && <div style={{ color: 'red' }}>{localValidationErrors.status}</div>}
              </div>
            )}

            {/* Reason */}
            <div className="form-group">
              <label className="form-label">
                Reason <span className="text-danger">*</span>
              </label>

              <div style={{ position: "relative" }}>
                <textarea
                  name="reason"
                  className=" reason-height form-control"
                  rows={3}                      
                  style={{  paddingRight: "70px"}}
                  value={localReason}
                  onChange={handleInputChange}
                  disabled={localDisableAllFields}
                  maxLength={200}
                  title={localReason}
                />
                <span
                  style={{
                    position: "absolute",
                    right: "10px",
                    top:'50%',
                    transform:'translateY(-50%)',
                    fontSize: "0.8rem",
                    color: "#888",
                    pointerEvents: "none",
                  }}
                >
                  {(localReason?.length ?? 0)}/200
                </span>
              </div>

              {localValidationErrors?.reason && (
                <div style={{ color: "red" }}>{localValidationErrors.reason}</div>
              )}
            </div>

          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">Close</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={(disableAllExceptStatus ? false : localDisableAllFields || isSaving)}>{isSaving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>           
      </div>
    </div> 
    {/* <ToastContainer position="top-right" autoClose={3000} /> */}
      </>,
      document.getElementById("modal-root") as HTMLElement
    )
  : null;
};

export default LeaveModal;